import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDepartments } from '../../contexts/DepartmentContext';
import { userService } from '../../services/userService';
import { User } from '../../types/user';
import { Plus, Edit, Trash, Search } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import UserSelector from '../common/UserSelector';

const DepartmentsPage: React.FC = () => {
  const {
    departments,
    isLoading,
    error,
    fetchDepartments,
    addDepartment,
    updateDepartment,
    deleteDepartment
  } = useDepartments();
  const { theme } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<number | null>(null);
  const [currentDepartment, setCurrentDepartment] = useState<Partial<any>>({});
  const [selectedManagerUser, setSelectedManagerUser] = useState<User | null>(null);
  const [selectedMemberUsers, setSelectedMemberUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const loadSelectedUsers = async (managerId: number | null | undefined, memberIds: number[]) => {
    try {
      // Load manager user if exists
      if (managerId) {
        const managerUser = await userService.getUser(managerId);
        setSelectedManagerUser(managerUser);
      } else {
        setSelectedManagerUser(null);
      }

      // Load member users if exist
      if (memberIds && memberIds.length > 0) {
        const memberUsers = await Promise.all(memberIds.map((id) => userService.getUser(id)));
        setSelectedMemberUsers(memberUsers);
      } else {
        setSelectedMemberUsers([]);
      }
    } catch (error) {
      console.error('Error loading selected users:', error);
    }
  };

  const handleSave = async () => {
    try {
      if (currentDepartment.id) {
        await updateDepartment(currentDepartment.id, currentDepartment);
      } else {
        await addDepartment(currentDepartment as any);
      }
      // Refetch departments to get updated manager information
      await fetchDepartments();
      setIsModalOpen(false);
      setSelectedManagerUser(null);
      setSelectedMemberUsers([]);
    } catch (error: any) {
      console.error('Error saving department:', error);
      const errorMessage =
        error?.response?.data?.error || error?.message || 'Failed to save department';
      alert(`Failed to save department: ${errorMessage}`);
    }
  };

  const handleDeleteClick = (id: number) => {
    setDepartmentToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (departmentToDelete === null) return;
    try {
      await deleteDepartment(departmentToDelete);
      setIsDeleteModalOpen(false);
      setDepartmentToDelete(null);
    } catch (error) {
      console.error('Error deleting department:', error);
      alert('Failed to delete department. Ensure no users are assigned to it.');
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDepartmentToDelete(null);
  };

  const filteredDepartments = (departments || []).filter(
    (dept) =>
      dept.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Departments
        </h1>
        <button
          onClick={() => {
            setCurrentDepartment({ member_ids: [] });
            setSelectedManagerUser(null);
            setSelectedMemberUsers([]);
            setIsModalOpen(true);
          }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700"
        >
          <Plus size={20} />
          Add Department
        </button>
      </div>

      <div className="mb-6 relative">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={20}
        />
        <input
          type="text"
          placeholder="Search departments..."
          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {error && (
        <div
          className={`border px-4 py-3 rounded mb-4 ${theme === 'dark' ? 'bg-red-900/30 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}
        >
          {error}
        </div>
      )}

      {isLoading ? (
        <div
          className={`text-center py-10 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}
        >
          Loading...
        </div>
      ) : (
        <div
          className={`rounded-lg shadow overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
        >
          <table
            className={`min-w-full divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}
          >
            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Name
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Description
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Manager
                </th>
                <th
                  className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              className={`divide-y ${theme === 'dark' ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}
            >
              {filteredDepartments.map((dept) => (
                <tr
                  key={dept.id}
                  className={theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}
                >
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                  >
                    {dept.name}
                  </td>
                  <td
                    className={`px-6 py-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    {dept.description || '-'}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    {dept.manager_name ? (
                      <div className="flex items-center">
                        <div
                          className={`h-8 w-8 rounded-full flex items-center justify-center mr-2 ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-500'}`}
                        >
                          {dept.manager_name.charAt(0)}
                        </div>
                        <div>
                          <div
                            className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                          >
                            {dept.manager_name}
                          </div>
                          <div
                            className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}
                          >
                            {dept.manager_email}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">No manager assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={async () => {
                        setCurrentDepartment({ ...dept });
                        await loadSelectedUsers(dept.manager_id, dept.member_ids || []);
                        setIsModalOpen(true);
                      }}
                      className={`mr-4 ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-900'}`}
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(dept.id)}
                      className={
                        theme === 'dark'
                          ? 'text-red-400 hover:text-red-300'
                          : 'text-red-600 hover:text-red-900'
                      }
                    >
                      <Trash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
            <div
              className={`rounded-lg p-6 w-full max-w-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
            >
              <h2
                className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
              >
                {currentDepartment.id ? 'Edit Department' : 'Add Department'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    value={currentDepartment.name || ''}
                    onChange={(e) =>
                      setCurrentDepartment({ ...currentDepartment, name: e.target.value })
                    }
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    Description
                  </label>
                  <textarea
                    value={currentDepartment.description || ''}
                    onChange={(e) =>
                      setCurrentDepartment({ ...currentDepartment, description: e.target.value })
                    }
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    rows={3}
                  />
                </div>

                <div>
                  <UserSelector
                    label="Manager"
                    fetchUsers={userService.searchUsers}
                    selectedIds={currentDepartment.manager_id || 0}
                    selectedUsers={selectedManagerUser ? [selectedManagerUser] : []}
                    onChange={(id, users) => {
                      setCurrentDepartment({ ...currentDepartment, manager_id: id as number });
                      setSelectedManagerUser(users[0] || null);
                    }}
                    placeholder="Select a manager..."
                  />
                </div>

                <div>
                  <UserSelector
                    label="Team Members"
                    fetchUsers={userService.searchUsers}
                    selectedIds={currentDepartment.member_ids || []}
                    selectedUsers={selectedMemberUsers}
                    onChange={(ids, users) => {
                      setCurrentDepartment({ ...currentDepartment, member_ids: ids as number[] });
                      setSelectedMemberUsers(users);
                    }}
                    multiple={true}
                    placeholder="Select team members..."
                    excludeIds={currentDepartment.manager_id ? [currentDepartment.manager_id] : []}
                  />
                  <p
                    className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    Select users to assign to this department.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className={`px-4 py-2 ${theme === 'dark' ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  disabled={!currentDepartment.name}
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {isDeleteModalOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
            <div
              className={`rounded-lg p-6 w-full max-w-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
            >
              <h2
                className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
              >
                Confirm Delete
              </h2>
              <p className={`mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Are you sure you want to delete this department? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className={`px-4 py-2 border rounded-md ${theme === 'dark' ? 'text-gray-300 hover:text-gray-100 border-gray-600 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-800 border-gray-300 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DepartmentsPage;
